import { formatDate, getNetworkTime, getTimestamp } from '../time';
import { getLoggerUrl, isString, isUndefined, isNumber, REPORT_TYPE, buildSSOPackage } from '../utils';
import { http } from '../http';
import promiseRetry from '../retry';
import { LOG_LEVEL, LOGGER_CMD_TYPE, IS_WORKER, version, IS_WORKLET } from '../../common/constants';
import Logger from './logger';

import { IS_IOS, IS_ANDROID, IS_HEADLESS_CHROME } from '../environment';
import innerEmitter from '../../inner-emitter/inner-emitter';
import INNER_EVENT from '../../inner-emitter/inner-event';
import ssoManager from '../sso/sso-manager';

const LOG_PRE_TAG = '%cTRTC%c%s';
const LOG_PRE_TAG_STYLE = 'padding: 1px 4px;border-radius: 3px;color: #fff;background: #1E88E5;';
const LOG_STYLE = 'display: inline';

/**
 * 日志实例，每输出一次日志，都会生成一个日志实例。
 * @property {string} log 日志内容
 * @property {LOG_LEVEL} level 日志等级
 * @property {string} [userId] 日志 userId
 * @property {string} [sdkAppId] 日志 sdkAppId
 * @property {boolean} [forAllJoinedClients] 是否向所有已进房的 client 上报日志。
 * 用于公共模块的日志上报，例如 utils, environment getUserMedia 等
 * tips: Client, Connection, SignalChannel 等模块有明确的上报 userId，
 * 因此这些模块的 forAllJoinedClients 为 false
 * @class Log
 */
export interface ILog { uploaded?: boolean, log: string, level: LOG_LEVEL, userId?: string, sdkAppId?: number, forAllJoinedClients?: boolean; }
/**
 * 公共日志上报模块，单例
 * 支持设置 LogLevel、开关日志上报、云控控制日志上报等功能
 * @class Logger
 */
class LoggerManager {
  // 移动端和 headless chrome 不打印 "TRTC" log tag
  static PRINT_LOG_TAG = !(IS_IOS || IS_ANDROID || IS_HEADLESS_CHROME);
  private _isEnableUploadLog = true;
  private _localJoinedUser = new Map<string, { userId: string; sdkAppId: number; }>();
  private _queue: ILog[] = [];
  private _timeoutId = -1;
  private _logLevel: LOG_LEVEL = LOG_LEVEL.DEBUG;
  // 默认上报 info 及以上级别的日志
  private _logLevelToUpload: LOG_LEVEL = LOG_LEVEL.INFO;
  // 等进房结束（成功 or 失败）后，再进行上报，避免影响信令调度耗时
  constructor() {
    // 在主线程初始化 loggerManager
    if (!IS_WORKER && !IS_WORKLET) {
      this.checkURLParam();
      this.installEvents();
    }
  }

  get isAbleToUpload() {
    return this._isEnableUploadLog && this._timeoutId !== -1;
  }

  installEvents() {
    innerEmitter.on(INNER_EVENT.JOIN_SCHEDULE_SUCCESS, ({ schedule }) => {
      // 日志上报云控，云控优先级高于 URL param
      if (schedule?.config?.logLevelToUpload && LOG_LEVEL[schedule.config.logLevelToUpload]
      ) {
        this._logLevelToUpload = schedule.config.logLevelToUpload;
      }
    });
    innerEmitter.on(INNER_EVENT.JOIN_START, ({ params }) => {
      this.addJoinedUser({ userId: params.userId, sdkAppId: params.sdkAppId });
      this.startUpload();
    });
    innerEmitter.on(INNER_EVENT.LEAVE_SUCCESS, ({ room }) => {
      this.deleteJoinedUser(room.userId);
    });
  }

  /**
   * 开始上报
   *
   * 调用时机：主线程 room 进房成功后，worker 线程需要上报日志时。
   */
  startUpload() {
    if (this._timeoutId !== -1) return;
    this.uploadInterval();
  }

  /**
   * 进房成功后调用
   */
  addJoinedUser(option: { userId: string; sdkAppId: number; }) {
    this._localJoinedUser.set(option.userId, option);
    this.startUpload();
  }

  /**
   * 退房后调用
   */
  deleteJoinedUser(userId: string) {
    this._localJoinedUser.delete(userId);
  }


  uploadInterval() {
    this.upload().catch(() => { });
    this._timeoutId = window.setTimeout(() => this.uploadInterval(), 5000);
  }

  // stopUpload() {
  //   if (this._timeoutId !== -1) {
  //     clearTimeout(this._timeoutId);
  //     this._timeoutId = -1;
  //   }
  // }

  /**
   * 获取待上传的日志
   *
   * @return {Object} result
   * @memberof Logger
   */
  getLogsToUpload() {
    const result: { map: Map<string, { userId: string; sdkAppId: number; logs: ILog[]; }>; splicedQueue: ILog[]; } = {
      map: new Map(), // Map<userId, { userId, sdkAppId, logs[] }>
      splicedQueue: [] // logs[] 从日志队列里取出来的日志列表，当上传请求失败时，可将其从头插入日志队列。
    };
    if (this._queue[0].forAllJoinedClients && this._localJoinedUser.size === 0) {
      return result;
    }
    let count = 0;
    for (; count < this._queue.length; count++) {
      if (count === 50) {
        break;
      }

      const log = this._queue[count];
      if (log.forAllJoinedClients) {
        // 向已进房的 client 上报。
        this._localJoinedUser.forEach(({ userId, sdkAppId }) => {
          if (result.map.has(userId)) {
            result.map.get(userId)!.logs.push(log);
          } else {
            result.map.set(userId, { userId, sdkAppId, logs: [log] });
          }
        });
      } else if (isString(log.userId) && isNumber(log.sdkAppId)) {
        const { userId, sdkAppId } = log;
        if (result.map.has(userId)) {
          result.map.get(userId)!.logs.push(log);
        } else {
          result.map.set(userId, { userId, sdkAppId, logs: [log] });
        }
      }
    }
    if (result.map.size > 0) {
      // 将已遍历的日志，在日志队列中出列
      result.splicedQueue = this._queue.splice(0, count);
    }
    return result;
  }

  async upload() {
    if (this._queue.length === 0 || !this._isEnableUploadLog) {
      return;
    }

    const { map, splicedQueue } = this.getLogsToUpload();
    if (map.size === 0) {
      return;
    }

    try {
      const data = [...map.values()];
      for (let i = 0; i < data.length; i++) {
        const { userId, sdkAppId, logs } = data[i];
        const json = {
          timestamp: getTimestamp(),
          sdkAppId: String(sdkAppId),
          userId,
          version,
          log: logs.map(item => item.log).join('\n')
        };
        const originToUpload = JSON.stringify(json);
        const toUpload = ssoManager.enable ? buildSSOPackage(json, REPORT_TYPE.LOG, sdkAppId) : originToUpload;
        await this.uploadLogWithRetry(
          toUpload,
          sdkAppId,
          toUpload instanceof Uint8Array,
          originToUpload
        );
        logs.forEach(log => (log.uploaded = true));
      }
    } catch (error) {
      // ignore
    }

    // 上报成功、失败后，检查是否有未上报的日志，有则放回队列重新上报。
    const unUploadedLogs = splicedQueue.filter(log => !log.uploaded);
    if (unUploadedLogs.length > 0) {
      this._queue = unUploadedLogs.concat(this._queue);
    }
  }

  uploadLogWithRetry(toUpload: string | Uint8Array, sdkAppId: number, isSSO: boolean, originToUpload: string) {
    return promiseRetry({
      retryFunction: () => http({ url: getLoggerUrl(sdkAppId, LOGGER_CMD_TYPE.LOG), body: toUpload, timeout: 5000, priority: 'low' }).then(response => {
        if (isSSO && response.data !== 'ok') {
          ssoManager.handleUploadFailed();
          this.uploadLogWithRetry(originToUpload, sdkAppId, false, originToUpload);
        }
      }),
      settings: {
        retries: 3, // 重试3次
        timeout: 2000
      },
      onError: ({ retry }) => {
        retry();
      }
    })();
  }

  getPrefix(level: number) {
    const now = new Date();
    now.setTime(getNetworkTime());
    return `[${formatDate(now)}] <${LOG_LEVEL[level]}>`;
  }

  getLogLevel() {
    return this._logLevel;
  }

  setLogLevel(level: number) {
    if (isUndefined(LOG_LEVEL[level])) {
      return;
    }
    if (this._logLevel !== level) {
      this.info('setLogLevel', level);
    }
    this._logLevel = level;
  }

  enableUploadLog() {
    this._isEnableUploadLog = true;
  }

  disableUploadLog() {
    this.warn('disableUploadLog');
    this._isEnableUploadLog = false;
  }
  logChunkToString(chunk: any) {
    if (isString(chunk)) {
      return chunk;
    }
    try {
      return chunk instanceof Error ? chunk.toString() : JSON.stringify(chunk);
    } catch (err) {
      return '';
    }
  }

  addLogToQueue(level: LOG_LEVEL, log: any[], forAllJoinedClients = true, userId?: string, sdkAppId?: number) {
    const item = {
      log: log.reduce((acc, c) => `${acc} ${this.logChunkToString(c)}`.trim(), ''),
      level,
      userId,
      sdkAppId,
      forAllJoinedClients
    };
    innerEmitter.emit(INNER_EVENT.LOG, { log: item });
    if (this._isEnableUploadLog && level >= this._logLevelToUpload) {
      this._queue.push(item);
    }
  }
  log(level: LOG_LEVEL, log: any[], forAllJoinedClients = true, userId?: string, sdkAppId?: number) {
    log.unshift(this.getPrefix(level));

    this.addLogToQueue(level, log, forAllJoinedClients, userId, sdkAppId);
    if (level < this._logLevel) {
      return;
    }
    const funName = (LOG_LEVEL[level]?.toLowerCase() || 'info') as 'info' | 'debug' | 'warn' | 'error';
    if (LoggerManager.PRINT_LOG_TAG) {
      // eslint-disable-next-line no-console
      console[funName](LOG_PRE_TAG, LOG_PRE_TAG_STYLE, LOG_STYLE, ...log);
    } else {
      // eslint-disable-next-line no-console
      console[funName](...log);
    }
  }

  debug(...log: any[]): void {
    this.log(LOG_LEVEL.DEBUG, log);
  }

  info(...log: any[]) {
    this.log(LOG_LEVEL.INFO, log);
  }

  warn(...log: any[]) {
    this.log(LOG_LEVEL.WARN, log);
  }

  error(...log: any[]) {
    this.log(LOG_LEVEL.ERROR, log);
  }

  createLogger(options: { parent?: Logger, id: string, userId?: string, remoteUserId?: string; sdkAppId?: number, type?: 'main' | 'auxiliary', isLocal?: boolean; }) {
    if (options.parent instanceof Logger) {
      const { parent, ...rest } = options;
      const logger = new Logger(rest);
      logger.bindParent(parent);
      return logger;
    }
    return new Logger(options);
  }

  /**
   * 检查 URL param，支持通过 url 参数来控制日志上报 level
   * @memberof Logger
   */
  checkURLParam() {
    const urlParam = new URLSearchParams(location.search);
    const tempLogLevelToUpload = urlParam.get('logLevelToUpload');
    const logLevelToUpload = tempLogLevelToUpload ? Number(tempLogLevelToUpload) : -1;
    if (LOG_LEVEL[logLevelToUpload]) {
      this._logLevelToUpload = logLevelToUpload;
    }
  }

  getQueue() {
    return this._queue;
  }
}

export default new LoggerManager();
